// Helpers for mapping between API comparators/values and UI representations.
// Regex comparators use composite UI keys (e.g. regex_startsWith) split at the API boundary.
import { endOfDay, startOfDay } from "date-fns";

import { DEFAULT_TIMEZONE, convertToLocalZonedTime, convertToZonedUTCTime } from "@performyard/utils-isomorphic";

import type { ComparatorOption, DateSubType, IEmployeeField, QueryCondition } from "./types";
import { DATE_TYPES, NUMERIC_TYPES, PEOPLE_TYPES, TEXT_TYPES } from "./types";

import {
	ANNIVERSARY_COMPARATORS,
	DEFAULT_COMPARATORS,
	IS_MANAGER_FIELD_ID,
	IS_MANAGER_FIELD_NAME,
	NUMERIC_COMPARATORS,
	RELATIVE_DATE_COMPARATORS,
	STATIC_DATE_COMPARATORS,
	TEXT_COMPARATORS,
} from "./constants";

export const getFieldType = (field: QueryCondition["field"], employeeFields: IEmployeeField[]): string => {
	if (field === IS_MANAGER_FIELD_ID) return "boolean";
	if (typeof field === "object" && "oid" in field) {
		const found = employeeFields.find((ef) => ef._id === field.oid);
		return found?.type ?? "";
	}
	return "";
};

// Returns the employee id for a person-field condition (value is stored as { oid }), else null.
export const getPersonOid = (condition: QueryCondition, employeeFields: IEmployeeField[]): string | null => {
	if (!PEOPLE_TYPES.includes(getFieldType(condition.field, employeeFields))) return null;
	const { value } = condition;
	return value && typeof value === "object" && "oid" in value ? value.oid : null;
};

export const getFieldName = (field: QueryCondition["field"], employeeFields: IEmployeeField[]): string => {
	if (field === IS_MANAGER_FIELD_ID) return IS_MANAGER_FIELD_NAME;
	if (typeof field === "object" && "oid" in field) {
		const found = employeeFields.find((ef) => ef._id === field.oid);
		return found?.name ?? "";
	}
	return "";
};

export const getFieldId = (field: QueryCondition["field"]): string => {
	if (field === IS_MANAGER_FIELD_ID) return IS_MANAGER_FIELD_ID;
	if (typeof field === "object" && "oid" in field) return field.oid;
	return "";
};

export const getFieldOptions = (field: QueryCondition["field"], employeeFields: IEmployeeField[]): string[] => {
	if (typeof field === "object" && "oid" in field) {
		const found = employeeFields.find((ef) => ef._id === field.oid);
		return found?.options ?? [];
	}
	return [];
};

export const getComparatorsForFieldType = (fieldType: string, dateSubType?: DateSubType): ComparatorOption[] => {
	if (NUMERIC_TYPES.includes(fieldType)) return NUMERIC_COMPARATORS;
	if (TEXT_TYPES.includes(fieldType)) return TEXT_COMPARATORS;
	if (DATE_TYPES.includes(fieldType)) {
		if (dateSubType === "static") return STATIC_DATE_COMPARATORS;
		if (dateSubType === "anniversary") return ANNIVERSARY_COMPARATORS;
		return RELATIVE_DATE_COMPARATORS;
	}
	return DEFAULT_COMPARATORS;
};

export const toUIComparator = (apiComparator: string, apiValue: QueryCondition["value"]): string => {
	if (apiComparator === "regex" && typeof apiValue === "string") {
		if (apiValue.startsWith("^")) return "regex_startsWith";
		if (apiValue.endsWith("$")) return "regex_endsWith";
		return "regex_contains";
	}
	if (apiComparator === "not_regex" && typeof apiValue === "string") {
		if (apiValue.startsWith("^")) return "not_regex_notStartsWith";
		if (apiValue.endsWith("$")) return "not_regex_notEndsWith";
		return "not_regex_notContains";
	}
	return apiComparator;
};

export const toAPIComparator = (uiComparator: string): string => {
	if (uiComparator.startsWith("regex_")) return "regex";
	if (uiComparator.startsWith("not_regex_")) return "not_regex";
	return uiComparator;
};

export const wrapRegexValue = (uiComparator: string, rawValue: string): string => {
	if (uiComparator === "regex_startsWith" || uiComparator === "not_regex_notStartsWith") return `^${rawValue}`;
	if (uiComparator === "regex_endsWith" || uiComparator === "not_regex_notEndsWith") return `${rawValue}$`;
	return rawValue;
};

export const unwrapRegexValue = (apiValue: string): string => {
	if (apiValue.startsWith("^")) return apiValue.slice(1);
	if (apiValue.endsWith("$")) return apiValue.slice(0, -1);
	return apiValue;
};

export const detectDateSubType = (value: QueryCondition["value"]): DateSubType => {
	if (value && typeof value === "object") {
		if ("date" in value) return "static";
		if ("anniversary_date" in value) return "anniversary";
		if ("relative_date" in value) return "relative";
	}
	return "static";
};

export const adjustDateToDayBoundary = (date: Date, comparator: string, timezone: string): string => {
	// Snap the picked calendar day to the start (before/on) or end (after) of that day in the COMPANY
	// timezone, since that's when the automation runs (PY-13538).
	const zoned = convertToLocalZonedTime(date, timezone);
	const boundary = comparator === "gt" ? endOfDay(zoned) : startOfDay(zoned);
	return convertToZonedUTCTime(boundary, timezone).toISOString();
};

export const getComparatorLabel = (apiComparator: string, apiValue: QueryCondition["value"]): string => {
	const uiKey = toUIComparator(apiComparator, apiValue);
	const isDateValue =
		!!apiValue &&
		typeof apiValue === "object" &&
		("date" in apiValue || "relative_date" in apiValue || "anniversary_date" in apiValue);
	if (isDateValue) {
		const dateSubType = detectDateSubType(apiValue);
		const dateComparators =
			dateSubType === "static"
				? STATIC_DATE_COMPARATORS
				: dateSubType === "anniversary"
					? ANNIVERSARY_COMPARATORS
					: RELATIVE_DATE_COMPARATORS;
		const dateLabel = dateComparators.find((c) => c.value === uiKey)?.label;
		if (dateLabel) return dateLabel;
	}
	const allComparators = [
		...DEFAULT_COMPARATORS,
		...TEXT_COMPARATORS,
		...NUMERIC_COMPARATORS,
		...STATIC_DATE_COMPARATORS,
		...RELATIVE_DATE_COMPARATORS,
		...ANNIVERSARY_COMPARATORS,
	];
	return allComparators.find((c) => c.value === uiKey)?.label ?? apiComparator;
};

export const getDisplayValue = (
	condition: QueryCondition,
	employeeFields: IEmployeeField[],
	displayNames?: Record<string, string>,
	timezone: string = DEFAULT_TIMEZONE
): string => {
	const { value, comparator } = condition;
	if (value === null || value === undefined) return "";
	if (typeof value === "boolean") return value ? "True" : "False";
	if (typeof value === "number") return String(value);

	if (typeof value === "string") {
		if (comparator === "regex" || comparator === "not_regex") {
			return unwrapRegexValue(value);
		}
		return value;
	}

	if (typeof value === "object") {
		if ("date" in value) {
			const zoned = convertToLocalZonedTime(new Date(value.date), timezone);
			return zoned.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
		}
		if ("relative_date" in value) {
			const length = Math.abs(value.relative_date);
			const unit = value.relative_date_unit;
			const unitLabel = unit.charAt(0).toUpperCase() + unit.slice(1);
			return `${length} ${length === 1 ? unitLabel.slice(0, -1) : unitLabel} Ago`;
		}
		if ("anniversary_date" in value) {
			if (comparator === "eq_anniversary" && value.anniversary_date === 0) return "";
			const length = Math.abs(value.anniversary_date);
			const unit = value.anniversary_date_unit;
			const unitLabel = unit.charAt(0).toUpperCase() + unit.slice(1);
			return `${length} ${length === 1 ? unitLabel.slice(0, -1) : unitLabel}`;
		}
		if ("oid" in value) {
			return displayNames?.[value.oid] ?? value.oid;
		}
	}

	return String(value);
};
